import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const showcaseApps = [
  {
    title: 'Book Library',
    description: 'A digital book library with search, categories, and reading lists',
    image: '/showcase_1.png',
  },
  {
    title: 'Design Studio',
    description: 'AI-powered design studio with mood boards and color palettes',
    image: '/showcase_2.png',
  },
  {
    title: 'Project Manager',
    description: 'Kanban boards, task tracking, and team collaboration',
    image: '/showcase_3.png',
  },
  {
    title: 'E-Commerce Store',
    description: 'Full storefront with product catalog and shopping cart',
    image: '/showcase_4.png',
  },
];

const AppShowcase = () => {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % showcaseApps.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + showcaseApps.length) % showcaseApps.length);
  }, []);

  useEffect(() => {
    const interval = setInterval(next, 5000);
    return () => clearInterval(interval);
  }, [next]);

  return (
    <div className="relative w-full" data-testid="app-showcase">
      {/* Trusted badge */}
      <div className="flex items-center justify-center gap-2 mb-5">
        <div className="flex -space-x-2">
          {[
            'bg-gradient-to-br from-blue-500 to-cyan-500',
            'bg-gradient-to-br from-purple-500 to-pink-500',
            'bg-gradient-to-br from-emerald-500 to-teal-500',
          ].map((gradient, i) => (
            <div
              key={i}
              className={`w-8 h-8 rounded-full ${gradient} border-2 border-white`}
            />
          ))}
        </div>
        <span className="text-foreground/80 text-sm font-medium">
          Trusted by <span className="text-foreground font-bold">50K+</span> Users
        </span>
      </div>

      {/* Showcase card */}
      <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-2xl shadow-blue-500/10">
        <div className="relative aspect-[3/2] overflow-hidden">
          {showcaseApps.map((app, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-700 ${
                index === current
                  ? 'opacity-100 translate-x-0'
                  : index < current
                  ? 'opacity-0 -translate-x-full'
                  : 'opacity-0 translate-x-full'
              }`}
            >
              <img
                src={app.image}
                alt={app.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {/* Gradient overlay at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
            </div>
          ))}
        </div>

        {/* Title bar */}
        <div className="px-5 py-4 flex items-center justify-between border-t border-border">
          <div>
            <h4 className="text-foreground font-semibold text-sm">{showcaseApps[current].title}</h4>
            <p className="text-muted-foreground text-xs mt-0.5">{showcaseApps[current].description}</p>
          </div>
          <div className="text-xs text-emerald-400 font-medium px-2 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20">
            Built with SuperTool
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 mt-5">
        <button
          onClick={prev}
          className="w-9 h-9 rounded-full border border-input bg-muted flex items-center justify-center hover:border-slate-500 hover:bg-accent transition-all"
          data-testid="showcase-prev"
        >
          <ChevronLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <div className="flex gap-2">
          {showcaseApps.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrent(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === current
                  ? 'bg-blue-500 w-8'
                  : 'bg-gray-300 w-3 hover:bg-gray-400'
              }`}
              data-testid={`showcase-dot-${index}`}
            />
          ))}
        </div>
        <button
          onClick={next}
          className="w-9 h-9 rounded-full border border-input bg-muted flex items-center justify-center hover:border-slate-500 hover:bg-accent transition-all"
          data-testid="showcase-next"
        >
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
};

export default AppShowcase;
